import type { ProgramLevel, ProgramSubject } from '@lms/shared/types';

interface SubjectWriter {
  update: (id: string, data: Partial<ProgramSubject>) => Promise<ProgramSubject>;
  get: (id: string) => Promise<ProgramSubject>;
}

export class SubjectPrerequisiteVerificationError extends Error {
  readonly reason: 'mismatch' | 'unverified';

  constructor(reason: 'mismatch' | 'unverified') {
    super(`Subject prerequisite save verification: ${reason}`);
    this.name = 'SubjectPrerequisiteVerificationError';
    this.reason = reason;
  }
}

/** A 200 response is not sufficient: verify an explicitly changed relation by reading it back. */
export async function updateSubjectWithVerification(
  client: SubjectWriter,
  id: string,
  data: Partial<ProgramSubject>
): Promise<ProgramSubject> {
  const saved = await client.update(id, data);
  if (data.prerequisiteSubjectId === undefined) return saved;

  const matches = (subject: ProgramSubject) =>
    subject.id === id &&
    subject.prerequisiteSubjectId === data.prerequisiteSubjectId &&
    (data.prerequisiteSubjectId !== null || !subject.prerequisiteSubject);
  if (!matches(saved)) throw new SubjectPrerequisiteVerificationError('mismatch');

  let verified: ProgramSubject;
  try {
    verified = await client.get(id);
  } catch {
    throw new SubjectPrerequisiteVerificationError('unverified');
  }
  if (!matches(verified)) throw new SubjectPrerequisiteVerificationError('mismatch');
  return verified;
}

/** Patch only this confirmed relation, preserving sessions, media and evaluation data. */
export function applyVerifiedSubjectPrerequisite(
  levels: ProgramLevel[] | undefined,
  verified: ProgramSubject
): ProgramLevel[] | undefined {
  return levels?.map(level => ({
    ...level,
    stages: level.stages?.map(stage => ({
      ...stage,
      subjects: stage.subjects?.map(subject =>
        subject.id === verified.id
          ? {
              ...subject,
              prerequisiteSubjectId: verified.prerequisiteSubjectId,
              prerequisiteSubject: verified.prerequisiteSubject ?? undefined,
            }
          : subject
      ),
    })),
  }));
}
